<template>
  <CustomModal
    :isOpen="isOpen"
    :backdropDismiss="backdropDismiss"
    @close="$emit('close')"
  >
    <div class="flame-icon my-4">
      <img :src="flameAnimation" alt="Flame" />
    </div>

    <h2 class="text-lg md:text-xl font-bold streak-title">
      You are on a
      <span
        class="highlight border-zinc-300 dark:border-zinc-400 text-black dark:text-white"
      >
        <span>
          <span>
            <img :src="flameStatic" alt="Flame" class="w-4 h-4" />
          </span>
          <span>
            {{ streakCount || 0 }}
          </span>
        </span>
      </span>
      day streak!
    </h2>

    <div class="info-sections">
      <div class="info-section flex items-start mb-6">
        <div class="icon mr-4">
          <img :src="flameStatic" alt="flameStatic" />
        </div>
        <div class="content">
          <h3 class="text-md md:text-lg font-bold">Build your Streak</h3>
          <p class="text-gray-600 dark:text-gray-300 text-sm md:text-base">
            Stay active by posting and engaging daily to grow your Streak.
          </p>
        </div>
      </div>

      <div class="info-section flex items-start mb-6">
        <div class="icon mr-4">
          <img :src="trophy" alt="Trophy" />
        </div>
        <div class="content">
          <h3 class="text-md md:text-lg font-bold">Challenge yourself</h3>
          <p class="text-gray-600 dark:text-gray-300">
            Push your limits—how long can you keep your Streak going strong?
          </p>
        </div>
      </div>

      <div class="info-section flex items-start">
        <div class="icon mr-4">
          <img :src="coins" alt="coins" />
        </div>
        <div class="content">
          <h3 class="text-md md:text-lg font-bold">Earn rewards</h3>
          <p class="text-gray-600 dark:text-gray-300 text-sm md:text-base">
            The longer your Streak, the more rewards you unlock. Keep it going!
          </p>
        </div>
      </div>
    </div>
  </CustomModal>
</template>

<script setup>
import { ref } from "vue";
import flameAnimation from "@/assets/flame_animation.gif";
import flameStatic from "@/assets/flame_static.svg";
import trophy from "@/assets/trophy.svg";
import coins from "@/assets/coins.svg";
import CustomModal from "@/components/CustomModal.vue";

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  streakCount: {
    type: Number,
    default: 0,
  },
  backdropDismiss: {
    type: Boolean,
    default: true,
  },
});

defineEmits(["close"]);
</script>

<style scoped lang="scss">
// Flame icon styles
.flame-icon {
  border-radius: 9999px;
  aspect-ratio: 1/1;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0 30px 2px rgba(0, 0, 10, 0.1);
  width: 130px;
  height: 130px;
  margin: 0.5em auto;

  img {
    height: 90px;
    width: auto;
    display: inline-block;
  }
}

// Info sections styles
.info-sections {
  .info-section {
    .icon {
      img {
        padding-top: 0.75em;
        width: 40px;
        display: inline-block;
      }
    }

    &:last-child {
      margin-bottom: 0;
    }

    .content {
      h3 {
        @apply text-black dark:text-white;
      }
    }
  }
}

.streak-title {
  margin-top: 1em;
  margin-bottom: 1.5em;
  @apply text-black dark:text-white;
}

.highlight {
  display: inline-block;
  align-items: center;
  gap: 0.5em;
  border-width: 1px;
  border-radius: 12px;
  padding: 0.25em 0.5em;

  > span {
    display: flex;
    align-items: center;
    gap: 0.25em;

    > span {
      &:nth-child(2) {
        margin-top: -3px;
      }
    }
  }
}

// Compact mode adjustments
:deep(.compact-mode) {
  .flame-icon {
    width: 90px;
    height: 90px;

    img {
      height: 60px;
    }
  }

  .info-sections {
    margin-top: 0.5em;

    .info-section {
      margin-bottom: 1em;

      .icon img {
        width: 30px;
        padding-top: 0.5em;
      }

      h3 {
        font-size: 1rem;
      }

      p {
        font-size: 0.85rem;
        line-height: 1.2;
      }
    }
  }

  h2.streak-title {
    font-size: 1.1rem;
    margin-top: 0.5em !important;
    margin-bottom: 1em !important;
  }
}

// Dark mode adjustments
body.dark {
  .flame-icon {
    box-shadow: 0 0 30px 2px rgba(255, 255, 255, 0.25);
  }
}
</style>
